// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useMemo, useState } from 'react';
import { Box, Collapse, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { SetFileFootprintsModeAction, FileFootprintsMode } from '../mapCommonActions';
import { getVisibleAllFootprints } from '../footprintSelectors';
import { KIND_ORDER } from './footprintGeoJson';
import FootprintKindRows from './FootprintKindRows';
import stylesDef from './styles';

const MODES: Array<{ value: FileFootprintsMode; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'hover', label: 'On hover' },
  { value: 'always', label: 'Always on' },
];

const levelFor = (on: boolean, muted: boolean): FileFootprintsMode => {
  if (muted) return 'off';
  return on ? 'always' : 'hover';
};

// The admin pane's extents control: one GLOBAL level for the whole view
// ('off' = click-only, 'hover' = hidden at rest, 'always' = all drawn) plus
// the per-kind participation list behind the chevron. Replaces the floating
// on-map card on admin. The chevron stays enabled at every level: which
// kinds participate matters even while nothing draws at rest.
export const ExtentsPaneControl: React.FC = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const footprints = useAppSelector(getVisibleAllFootprints);
  const on = useAppSelector((state) => state.map.common.layers.fileFootprints);
  const muted = useAppSelector((state) => state.map.common.extentsHoverMuted);
  const hiddenKinds = useAppSelector((state) => state.map.common.hiddenFootprintKinds);
  const [expanded, setExpanded] = useState(false);

  const kinds = useMemo(
    () => [...new Set(footprints.map((fp) => fp.kind))]
      .sort((a, b) => KIND_ORDER[a] - KIND_ORDER[b]),
    [footprints],
  );

  if (!footprints.length) return null;

  const mode = levelFor(on, muted);
  const hasKindList = kinds.length >= 2;
  // Every present kind filtered out: the level reads as set while nothing
  // can ever draw. The control dims (still clickable - the level is kept
  // for when a kind comes back) and the note below says why.
  const allHidden = kinds.every((kind) => hiddenKinds.includes(kind));

  return (
    <Box>
      <Box sx={styles.paneControlRow}>
        <Typography sx={styles.switchLabel}>File Extents</Typography>
        <Box sx={styles.paneControlActions}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            sx={[allHidden && styles.modeGroupMuted]}
            onChange={(_, next: FileFootprintsMode | null) => {
              if (next) dispatch(SetFileFootprintsModeAction(next));
            }}
          >
            {MODES.map(({ value, label }) => (
              <ToggleButton key={value} value={value} sx={styles.modeButton}>{label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          {hasKindList && (
            <IconButton
              size="small"
              aria-label={expanded ? 'Collapse file extents' : 'Expand file extents'}
              sx={styles.chevronButton}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded
                ? <KeyboardArrowDownIcon fontSize="small" />
                : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>
      {allHidden && (
        <Typography sx={styles.allHiddenNote}>
          All data types are unchecked, so no extents can show at any level.
        </Typography>
      )}
      <Collapse in={hasKindList && expanded}>
        <FootprintKindRows kinds={kinds} variant="pane" />
      </Collapse>
    </Box>
  );
};

export default ExtentsPaneControl;
